import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Send, FileCheck2 } from "lucide-react";
import { fmtDate, fmtDateShort, fmtTimeRange } from "@/lib/format";
import { ContactDialog, JustifyDialog } from "@/components/attendance/AbsenceDialogs";

const STATUS = [
  ["assente", "Assenze", "bg-red-50 text-red-800 border-red-200", "text-red-700"],
  ["ritardo", "Ritardi", "bg-orange-50 text-orange-800 border-orange-200", "text-orange-700"],
  ["giustificato", "Giustificate", "bg-stone-100 text-stone-700 border-stone-200", "text-stone-700"],
  ["presente", "Presenze", "bg-emerald-50 text-emerald-800 border-emerald-200", "text-emerald-700"],
];
const BADGE = Object.fromEntries(STATUS.map(([k, , b]) => [k, b]));
const LABEL = { presente: "Presente", assente: "Assente", ritardo: "Ritardo", giustificato: "Giustificato" };
const CH = { whatsapp: "WhatsApp", email: "email", manuale: "a mano" };

function Counters({ counts, filter, setFilter }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {STATUS.map(([k, l, , cn]) => (
        <button key={k} type="button" onClick={() => setFilter(filter === k ? "all" : k)} data-testid={`att-counter-${k}`}
          className={`rounded-xl border p-4 text-left transition-colors ${filter === k ? "border-[#1C352D] bg-stone-50" : "border-stone-200 bg-white hover:bg-stone-50"}`}>
          <div className={`text-2xl font-semibold ${cn}`}>{counts[k] || 0}</div>
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">{l}</div>
        </button>
      ))}
    </div>
  );
}

function RecordRow({ r, onContact, onJustify }) {
  const open = (r.status === "assente" && !(r.justified_from && !r.unjustified_minutes)) || (r.status === "ritardo" && r.unjustified_minutes > 0);
  return (
    <li className="p-3 flex flex-wrap items-center gap-3 text-sm" data-testid={`att-record-${r.id}`}>
      <div className="min-w-[200px] flex-1">
        <Link to={`/scuola/presenze/${r.session_id}`} className="font-medium hover:underline">{r.course_name || r.session_title || "—"}</Link>
        <div className="text-[11px] text-stone-500">
          {fmtDateShort(r.session_date)}{r.session_start_time ? ` · ${fmtTimeRange(r.session_start_time, r.session_end_time)}` : ""}
          {r.arrival_time ? ` · arrivo ${r.arrival_time}` : ""}{r.note ? ` · ${r.note}` : ""}
        </div>
        {r.justified_from && (
          <div className="text-[11px] text-stone-600" data-testid={`att-partial-${r.id}`}>
            Giustificato {r.justified_from}–{r.justified_to}{r.unjustified_minutes ? ` · ${r.unjustified_minutes} min non giustificati` : " · nessun minuto scoperto"}
          </div>
        )}
        {r.status === "ritardo" && !r.justified_from && r.unjustified_minutes > 0 && (
          <div className="text-[11px] text-orange-700">{r.unjustified_minutes} min di ritardo non giustificati</div>
        )}
        {open && r.status === "assente" && (
          <div className={`text-[11px] mt-0.5 ${r.reported_at ? "text-emerald-700" : "text-amber-700"}`} data-testid={`att-reported-${r.id}`}>
            {r.reported_at ? `Segnalata ${fmtDate(r.reported_at)} via ${CH[r.reported_channel] || "—"}` : "Non ancora segnalata"}
          </div>
        )}
      </div>
      <Badge className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium border ${BADGE[r.status]}`}>{LABEL[r.status] || r.status}</Badge>
      {open && (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onContact(r)} data-testid={`att-contact-${r.id}`}><Send className="w-3.5 h-3.5 mr-1" />Segnala</Button>
          <Button size="sm" onClick={() => onJustify(r)} data-testid={`att-justify-${r.id}`}><FileCheck2 className="w-3.5 h-3.5 mr-1" />Giustifica</Button>
        </div>
      )}
    </li>
  );
}

export function StudentAttendanceTab({ student }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [course, setCourse] = useState("all");
  const [contact, setContact] = useState(null);
  const [justify, setJustify] = useState(null);
  const key = ["student-attendance-history", student.id];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: async () => (await api.get(`/attendance/students/${student.id}/history`)).data });
  const records = data?.records || [];
  const courses = useMemo(() => [...new Map(records.filter((r) => r.course_id).map((r) => [r.course_id, r.course_name || r.session_title])).entries()], [records]);
  const byCourse = records.filter((r) => course === "all" || r.course_id === course);
  const counts = byCourse.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});
  const shown = byCourse.filter((r) => filter === "all" || r.status === filter);
  const toRow = (r) => ({
    record_id: r.id, student_name: `${student.first_name || ""} ${student.last_name || ""}`.trim(),
    phone: student.phone, email: student.email || student.account_email, guardian_name: student.guardian_name,
    guardian_phone: student.guardian_phone, guardian_email: student.guardian_email,
    course_name: r.course_name || r.session_title, date: r.session_date, start_time: r.session_start_time, end_time: r.session_end_time, arrival_time: r.status === "ritardo" ? r.arrival_time : null,
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["unjustified"] }); };
  const reportMut = useMutation({ mutationFn: ({ id, channel }) => api.post(`/attendance/records/${id}/reported`, { channel }), onSuccess: refresh });
  const justifyMut = useMutation({
    mutationFn: ({ id, reason, times }) => api.post(`/attendance/records/${id}/justify`, { reason, ...times }),
    onSuccess: () => { toast.success("Assenza giustificata"); setJustify(null); refresh(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (isLoading) return <Card className="p-6 text-sm text-stone-500">Caricamento presenze…</Card>;
  return (
    <div className="space-y-4" data-testid="student-attendance-tab">
      <Counters counts={counts} filter={filter} setFilter={setFilter} />
      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 border-b border-stone-100 flex flex-wrap items-center gap-2 justify-between">
          <span className="text-sm font-medium">Tutte le lezioni ({shown.length})</span>
          <div className="flex flex-wrap gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[160px]" data-testid="att-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                {STATUS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={course} onValueChange={setCourse}>
              <SelectTrigger className="w-[200px]" data-testid="att-filter-course"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i corsi</SelectItem>
                {courses.map(([id, n]) => <SelectItem key={id} value={id}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {shown.length === 0 ? (
          <div className="p-8 text-center text-sm text-stone-500" data-testid="att-empty">Nessuna lezione con questi filtri.</div>
        ) : (
          <ul className="divide-y divide-stone-100 max-h-[600px] overflow-y-auto">
            {shown.map((r) => <RecordRow key={r.id} r={r} onContact={(x) => setContact(toRow(x))} onJustify={(x) => setJustify(toRow(x))} />)}
          </ul>
        )}
      </Card>
      <ContactDialog row={contact} onClose={() => setContact(null)}
        onReported={(channel) => { reportMut.mutate({ id: contact.record_id, channel }); toast.success("Assenza segnalata"); setContact(null); }} />
      {justify && <JustifyDialog row={justify} pending={justifyMut.isPending} onClose={() => setJustify(null)}
        onConfirm={(reason, times) => justifyMut.mutate({ id: justify.record_id, reason, times })} />}
    </div>
  );
}
